import { useState } from 'react';
import type { EPIRequest, Incident, Denunciation } from '@/types';

type SubmissionItem =
  | (EPIRequest & { type: 'epi' })
  | (Incident & { type: 'incident' })
  | (Denunciation & { type: 'denunciation' });

interface RecentSubmissionsProps {
  items: SubmissionItem[];
}

const statusColors: Record<string, string> = {
  Pendente: 'bg-amber-100 text-amber-700',
  Aprovado: 'bg-emerald-100 text-emerald-700',
  Negado: 'bg-red-100 text-red-700',
  'Em análise': 'bg-blue-100 text-blue-700',
  'Concluído': 'bg-emerald-100 text-emerald-700',
};

type TabType = 'all' | 'epi' | 'incident' | 'denunciation';

const tabs: { key: TabType; label: string; icon: string }[] = [
  { key: 'all', label: 'Todos', icon: 'ri-file-list-3-line' },
  { key: 'epi', label: 'EPI', icon: 'ri-tools-line' },
  { key: 'incident', label: 'Incidentes', icon: 'ri-alarm-warning-line' },
  { key: 'denunciation', label: 'Denúncias', icon: 'ri-shield-user-line' },
];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function SubmissionRow({ item }: { item: SubmissionItem }) {
  const getTitle = () => {
    if (item.type === 'epi') return `Solicitação de EPI - ${item.setor}`;
    if (item.type === 'incident') return `Incidente - ${item.userName}`;
    return item.identificar ?`Denúncia de ${item.nomeIdentificado}` : 'Denúncia Anônima';
  };

  const getStatus = () => {
    if (item.type === 'epi') {
      const s = (item as EPIRequest).status;
      return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[s]}`}>{s}</span>;
    }
    if (item.type === 'incident') return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[item.status]}`}>{item.status}</span>;
    return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[item.status]}`}>{item.status}</span>;
  };

  const getTypeBadge = () => {
    if (item.type === 'epi') return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-portal-purple-50 text-portal-purple">EPI</span>;
    if (item.type === 'incident') return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">Incidente</span>;
    return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-500">Denúncia</span>;
  };

  return (
    <div className="flex items-center gap-4 py-3 px-1 border-b border-portal-gray-100 last:border-0 hover:bg-portal-gray-50/50 transition-colors rounded-lg">
      <div className="w-8 h-8 rounded-lg bg-portal-gray-100 flex items-center justify-center flex-shrink-0">
        <i className={`${
          item.type === 'epi' ?'ri-tools-line text-portal-purple' :
          item.type === 'incident' ?'ri-alarm-warning-line text-amber-500' :
          'ri-shield-user-line text-red-400'
        } text-sm`}></i>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-portal-gray-800 truncate">{getTitle()}</p>
        <p className="text-xs text-portal-gray-400 mt-0.5">
          <span className="font-mono">{item.displayId || item.id}</span> - {formatDate(item.createdAt)}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {getTypeBadge()}
        {getStatus()}
      </div>
    </div>
  );
}

export default function RecentSubmissions({ items }: RecentSubmissionsProps) {
  const [activeTab, setActiveTab] = useState<TabType>('all');

  const filtered = activeTab === 'all'
    ?items
    : items.filter((i) => i.type === activeTab);

  return (
    <div className="bg-white rounded-xl border border-portal-gray-200 p-5">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-portal-gray-800">Atividades Recentes</h3>
        <div className="flex items-center gap-1 bg-portal-gray-100 rounded-lg p-0.5">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ?'bg-white text-portal-gray-800'
                  : 'text-portal-gray-500 hover:text-portal-gray-700'
              }`}
            >
              <i className={`${tab.icon} text-xs`}></i>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ?(
        <p className="text-sm text-portal-gray-400 py-8 text-center">Nenhuma atividade no período selecionado.</p>
      ) : (
        <div className="divide-y-0">
          {filtered.map((item) => (
            <SubmissionRow key={`${item.type}-${item.id}`} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
